"use client";

import { useEffect, useLayoutEffect, useMemo, useState } from "react";

import type { Service } from "@/types";

export type ServiceCountdownOfferOverlay = {
	label: string;
	variant: "active" | "ended";
};

export const SERVICE_COUNTDOWN_ENDED_GRACE_MS = 2 * 60 * 60 * 1000;
export const SERVICE_COUNTDOWN_STORAGE_KEY = "tx-countdown:lto";

function getCountdownBounds(service: Service) {
	const countdownStartsAtMs = service.countdownOffer?.startsAt
		? new Date(service.countdownOffer.startsAt).getTime()
		: null;
	const countdownEndsAtMs = service.countdownOffer?.endsAt
		? new Date(service.countdownOffer.endsAt).getTime()
		: null;

	return { countdownStartsAtMs, countdownEndsAtMs };
}

function getCountdownDurationSeconds(
	service: Service,
	countdownStartsAtMs: number | null,
	countdownEndsAtMs: number | null,
) {
	const fromField =
		typeof service.countdownOffer?.durationSeconds === "number" &&
		Number.isFinite(service.countdownOffer.durationSeconds)
			? Math.floor(service.countdownOffer.durationSeconds)
			: 0;
	if (fromField > 0) return fromField;
	if (
		countdownStartsAtMs !== null &&
		countdownEndsAtMs !== null &&
		!Number.isNaN(countdownStartsAtMs) &&
		!Number.isNaN(countdownEndsAtMs) &&
		countdownEndsAtMs > countdownStartsAtMs
	) {
		return Math.max(1, Math.floor((countdownEndsAtMs - countdownStartsAtMs) / 1000));
	}
	return 0;
}

function getCountdownStorageVersion(
	countdownStartsAtMs: number | null,
	countdownEndsAtMs: number | null,
	countdownDurationSeconds: number,
) {
	return `${countdownStartsAtMs ?? ""}:${countdownEndsAtMs ?? ""}:${countdownDurationSeconds}`;
}

/** Shared + per-service keys so banner and package card stay in sync. */
export function getServiceCountdownStorageKeys(serviceId: string) {
	return [SERVICE_COUNTDOWN_STORAGE_KEY, `tx-countdown:${serviceId}`] as const;
}

export function persistServiceCountdownDeadline(
	serviceId: string,
	deadlineMs: number,
	version: string,
) {
	if (typeof window === "undefined") return;
	const payload = JSON.stringify({ deadlineMs, version });
	try {
		for (const storageKey of getServiceCountdownStorageKeys(serviceId)) {
			window.localStorage.setItem(storageKey, payload);
		}
	} catch {
		// ignore storage write failures
	}
}

/**
 * Resolve the visitor's local countdown deadline (must match banner + package pricing).
 */
export function resolveClientDeadlineMs(
	service: Service,
	countdownStartsAtMs: number | null,
	countdownEndsAtMs: number | null,
	countdownDurationSeconds: number,
	nowMs: number,
	options?: { persist?: boolean },
) {
	if (
		typeof window === "undefined" ||
		service.countdownOffer?.enabled !== true ||
		!service._id
	) {
		return null;
	}

	if (service.countdownOffer.countdownUsesPromotionalEnd === true) {
		return countdownEndsAtMs !== null &&
			Number.isFinite(countdownEndsAtMs) &&
			countdownEndsAtMs > 0
			? countdownEndsAtMs
			: null;
	}

	if (countdownDurationSeconds <= 0) {
		return null;
	}

	const version = getCountdownStorageVersion(
		countdownStartsAtMs,
		countdownEndsAtMs,
		countdownDurationSeconds,
	);
	const storageKeys = getServiceCountdownStorageKeys(service._id);
	let resolvedDeadlineMs = nowMs + countdownDurationSeconds * 1000;
	try {
		for (const storageKey of storageKeys) {
			const raw = window.localStorage.getItem(storageKey);
			if (raw) {
				const parsed = JSON.parse(raw) as { deadlineMs?: number; version?: string };
				const storedDeadline = parsed.deadlineMs;
				if (
					typeof storedDeadline === "number" &&
					Number.isFinite(storedDeadline) &&
					storedDeadline > 0 &&
					parsed.version === version
				) {
					resolvedDeadlineMs = storedDeadline;
					break;
				}
			}
		}
	} catch {
		// ignore malformed storage
	}

	if (options?.persist !== false) {
		persistServiceCountdownDeadline(service._id, resolvedDeadlineMs, version);
	}

	return resolvedDeadlineMs;
}

export function getServiceCountdownOfferDiscountPercent(
	service: Service,
	clientDeadlineMs: number | null,
	nowMs: number,
) {
	const { countdownStartsAtMs, countdownEndsAtMs } = getCountdownBounds(service);
	const countdownEndedGraceExpired =
		clientDeadlineMs !== null &&
		nowMs >= clientDeadlineMs + SERVICE_COUNTDOWN_ENDED_GRACE_MS;
	const isInOfferCampaignWindow =
		service.countdownOffer?.enabled === true &&
		countdownStartsAtMs !== null &&
		countdownEndsAtMs !== null &&
		!Number.isNaN(countdownStartsAtMs) &&
		!Number.isNaN(countdownEndsAtMs) &&
		nowMs >= countdownStartsAtMs &&
		nowMs < countdownEndsAtMs;

	if (!isInOfferCampaignWindow || countdownEndedGraceExpired) return undefined;

	const configuredDiscountPercent = service.countdownOffer?.discountPercent;
	const withinClientCountdown = clientDeadlineMs === null || nowMs < clientDeadlineMs;
	if (
		!withinClientCountdown ||
		typeof configuredDiscountPercent !== "number" ||
		!Number.isFinite(configuredDiscountPercent) ||
		configuredDiscountPercent <= 0
	) {
		return undefined;
	}
	return Math.min(100, Math.max(0, Math.round(configuredDiscountPercent)));
}

export function useServiceCountdownOfferDiscounts(services: Service[]) {
	const [offerClock, setOfferClock] = useState(0);
	useEffect(() => {
		if (!services.some((service) => service.countdownOffer?.enabled === true)) return;
		const id = window.setInterval(() => setOfferClock((n) => n + 1), 250);
		return () => window.clearInterval(id);
	}, [services]);

	const countdownKey = useMemo(
		() =>
			services
				.map((service) => {
					const { countdownStartsAtMs, countdownEndsAtMs } = getCountdownBounds(service);
					const duration = getCountdownDurationSeconds(
						service,
						countdownStartsAtMs,
						countdownEndsAtMs,
					);
					return [
						service._id,
						service.countdownOffer?.enabled === true ? "1" : "0",
						countdownStartsAtMs ?? "",
						countdownEndsAtMs ?? "",
						duration,
						service.countdownOffer?.countdownUsesPromotionalEnd === true ? "1" : "0",
					].join(":");
				})
				.join("|"),
		[services],
	);
	const [clientDeadlineMsByServiceId, setClientDeadlineMsByServiceId] = useState<
		Record<string, number | null>
	>({});

	useLayoutEffect(() => {
		const nowMs = Date.now();
		const next: Record<string, number | null> = {};
		for (const service of services) {
			const { countdownStartsAtMs, countdownEndsAtMs } = getCountdownBounds(service);
			const duration = getCountdownDurationSeconds(
				service,
				countdownStartsAtMs,
				countdownEndsAtMs,
			);
			next[service._id] = resolveClientDeadlineMs(
				service,
				countdownStartsAtMs,
				countdownEndsAtMs,
				duration,
				nowMs,
			);
		}
		setClientDeadlineMsByServiceId(next);
	}, [countdownKey, services]);

	return useMemo(() => {
		const nowMs = Date.now();
		const next: Record<string, number> = {};
		for (const service of services) {
			const percent = getServiceCountdownOfferDiscountPercent(
				service,
				clientDeadlineMsByServiceId[service._id] ?? null,
				nowMs,
			);
			if (percent === undefined) continue;
			next[service._id] = percent;
			if (service.id) next[service.id] = percent;
		}
		return next;
	}, [clientDeadlineMsByServiceId, offerClock, services]);
}

/**
 * Same rules as package pricing / countdown: server promotional window + client localStorage deadline.
 */
export function useServiceCountdownOfferOverlay(service: Service): ServiceCountdownOfferOverlay | null {
	const { countdownStartsAtMs, countdownEndsAtMs } = getCountdownBounds(service);

	const countdownDurationSeconds = useMemo(() => {
		return getCountdownDurationSeconds(service, countdownStartsAtMs, countdownEndsAtMs);
	}, [countdownEndsAtMs, countdownStartsAtMs, service.countdownOffer?.durationSeconds]);

	const [, setOfferClock] = useState(0);
	useEffect(() => {
		if (service.countdownOffer?.enabled !== true) return;
		const id = window.setInterval(() => setOfferClock((n) => n + 1), 250);
		return () => window.clearInterval(id);
	}, [service.countdownOffer?.enabled]);

	const [clientDeadlineMs, setClientDeadlineMs] = useState<number | null | undefined>(undefined);
	useLayoutEffect(() => {
		setClientDeadlineMs(
			resolveClientDeadlineMs(
				service,
				countdownStartsAtMs,
				countdownEndsAtMs,
				countdownDurationSeconds,
				Date.now(),
			),
		);
	}, [
		countdownDurationSeconds,
		countdownEndsAtMs,
		countdownStartsAtMs,
		service._id,
		service.countdownOffer?.enabled,
		service.countdownOffer?.countdownUsesPromotionalEnd,
	]);

	const nowMs = Date.now();
	if (clientDeadlineMs === undefined) {
		return null;
	}

	const withinClientCountdown =
		clientDeadlineMs === null || nowMs < clientDeadlineMs;
	const countdownElapsed =
		clientDeadlineMs !== null && nowMs >= clientDeadlineMs;
	const countdownEndedGraceExpired =
		clientDeadlineMs !== null &&
		nowMs >= clientDeadlineMs + SERVICE_COUNTDOWN_ENDED_GRACE_MS;
	const isInOfferCampaignWindow =
		service.countdownOffer?.enabled === true &&
		countdownStartsAtMs !== null &&
		countdownEndsAtMs !== null &&
		!Number.isNaN(countdownStartsAtMs) &&
		!Number.isNaN(countdownEndsAtMs) &&
		nowMs >= countdownStartsAtMs &&
		nowMs < countdownEndsAtMs;

	if (!isInOfferCampaignWindow || countdownEndedGraceExpired) {
		return null;
	}

	const configuredDiscountPercent = service.countdownOffer?.discountPercent;
	const isLimitedOfferDiscountActive =
		withinClientCountdown &&
		typeof configuredDiscountPercent === "number" &&
		Number.isFinite(configuredDiscountPercent) &&
		configuredDiscountPercent > 0;
	const discountRounded = isLimitedOfferDiscountActive
		? Math.min(100, Math.max(0, Math.round(configuredDiscountPercent)))
		: undefined;

	if (countdownElapsed) {
		return { label: "OFFER ENDED", variant: "ended" };
	}
	if (discountRounded !== undefined) {
		return {
			label: `${discountRounded}% OFF - LIMITED OFFER`,
			variant: "active",
		};
	}
	return { label: "LIMITED OFFER", variant: "active" };
}
